import React, { forwardRef, useMemo } from 'react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { normalizeKey } from '../../utils/stringUtils';
import { DataGrid } from '../DataGrid/DataGrid';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DateCell } from '../DataGrid/Cells/DateCell';
import { dateSortValue, formatDateSortValue } from '../../utils/dateUtils';
import { dateSortComparator, withUniqueGridRowIds } from '../../utils/gridUtils';

interface RawDataGridProps {
  onDataUpdate?: (data: any[]) => void;
  data: any[];
  loading?: boolean;
}

function collectDataKeys(rows: { data?: Record<string, unknown> }[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    if (row.data && typeof row.data === 'object') {
      Object.keys(row.data).forEach((k) => keys.add(k));
    }
  }
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

function rawDataRowBaseId(item: { timestamp?: { date?: string; ticks?: number }; deviceId?: string }) {
  const ticks = item?.timestamp?.ticks;
  const date = item?.timestamp?.date;
  const deviceId = item?.deviceId;
  if (date != null && ticks != null) {
    return `${date}-${ticks}-${deviceId ?? ''}`;
  }
  if (date != null) {
    return date;
  }
  return undefined;
}

export const RawDataGrid = forwardRef<GridApiPremium | null, RawDataGridProps>(
  ({ data, loading }, ref) => {
    const columns: GridColDef[] = useMemo(() => {
      const dataKeys = collectDataKeys(data);

      const dynamicColumns = dataKeys.map((originalKey) => {
        const normalizedKey = normalizeKey(originalKey);

        return {
          field: normalizedKey,
          headerName: originalKey,
          flex: 1,
          minWidth: 150,
          valueGetter: (params: GridValueGetterParams) => {
            const value = params.row.data?.[normalizedKey];
            return typeof value === 'boolean' ? (value ? 'True' : 'False') : value; // Display 'True'/'False' for boolean
          },
        };
      });

      return [
        {
          field: 'rowDate',
          flex: 1,
          headerName: 'Date',
          minWidth: 150,
          valueGetter: (params) => dateSortValue(params.row.timestamp?.date),
          valueFormatter: ({ value }) => formatDateSortValue(value as number | null),
          sortComparator: dateSortComparator,
          renderCell: (params: GridRenderCellParams) => {
            return <DateCell dateValue={params.row.timestamp?.date} />;
          },
        },
        ...dynamicColumns,
      ];
    }, [data]);

    const rows = useMemo(() => {
      const normalizedRows = data.map((item) => {
        const normalizedData: Record<string, any> = {};

        Object.keys(item.data || {}).forEach((key) => {
          const normalizedKey = normalizeKey(key);
          normalizedData[normalizedKey] = item.data[key];
        });

        return {
          ...item,
          data: normalizedData,
        };
      });

      return withUniqueGridRowIds(normalizedRows, rawDataRowBaseId);
    }, [data]);

    return (
      <DataGrid
        ref={ref}
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.__gridRowId}
      />
    );
  },
);
